import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Lock, KeyRound, Phone, Smartphone } from 'lucide-react';
import Logo from '../components/Logo';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, loginWithToken } = useAuth();
  const [mode, setMode] = useState('password');
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [otpChannel, setOtpChannel] = useState('email');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpMobile, setOtpMobile] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { channel: otpChannel };
      if (otpChannel === 'email') {
        payload.email = otpEmail;
      } else {
        payload.mobile = otpMobile;
      }
      await axios.post(`${API}/auth/request-otp`, payload);
      toast.success(otpChannel === 'sms' ? 'OTP sent to your mobile!' : 'OTP sent to your email!');
      setMode('otp-verify');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { otp: otpCode, channel: otpChannel };
      if (otpChannel === 'email') {
        payload.email = otpEmail;
      } else {
        payload.mobile = otpMobile;
      }
      const res = await axios.post(`${API}/auth/verify-otp`, payload);
      loginWithToken(res.data.access_token, res.data.user);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      const payload = { channel: otpChannel };
      if (otpChannel === 'email') payload.email = otpEmail;
      else payload.mobile = otpMobile;
      await axios.post(`${API}/auth/resend-otp`, payload);
      toast.success('OTP resent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const otpTarget = otpChannel === 'sms' ? otpMobile : otpEmail;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-8" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
        </Button>

        <div className="bg-white rounded-xl border border-border p-8 shadow-sm">
          <div className="mb-6 text-center">
            <Logo className="h-10 mx-auto mb-4 object-contain" />
            <h1 className="text-3xl font-heading font-bold text-primary mb-2" data-testid="login-title">Welcome Back</h1>
            <p className="text-muted-foreground" data-testid="login-subtitle">Sign in to your account</p>
          </div>

          {/* Mode Toggle: Password | OTP */}
          <div className="flex rounded-lg bg-slate-100 p-1 mb-6" data-testid="login-mode-toggle">
            <button
              type="button"
              onClick={() => { setMode('password'); setOtpCode(''); }}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${mode === 'password' ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground'}`}
              data-testid="password-mode-btn"
            >
              <Lock className="w-3.5 h-3.5" /> Password
            </button>
            <button
              type="button"
              onClick={() => { setMode('otp-request'); setOtpCode(''); }}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${mode !== 'password' ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground'}`}
              data-testid="otp-mode-btn"
            >
              <KeyRound className="w-3.5 h-3.5" /> OTP Login
            </button>
          </div>

          {/* Password Login */}
          {mode === 'password' && (
            <form onSubmit={handlePasswordLogin} className="space-y-5" data-testid="login-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="mt-1.5" data-testid="email-input" placeholder="you@example.com" />
              </div>
              <div>
                <div className="flex justify-between items-center">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/reset-password" className="text-xs text-secondary hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
                </div>
                <Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required className="mt-1.5" data-testid="password-input" placeholder="Enter password" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11" data-testid="login-submit-button">
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          )}

          {/* OTP Request */}
          {mode === 'otp-request' && (
            <form onSubmit={handleRequestOTP} className="space-y-5" data-testid="otp-request-form">
              {/* Channel Toggle: Email | SMS */}
              <div className="flex rounded-md bg-slate-50 border p-0.5 mb-2" data-testid="otp-channel-toggle">
                <button
                  type="button"
                  onClick={() => setOtpChannel('email')}
                  className={`flex-1 py-1.5 px-2 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1 ${otpChannel === 'email' ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground hover:text-primary'}`}
                  data-testid="otp-email-channel-btn"
                >
                  <Mail className="w-3 h-3" /> Email
                </button>
                <button
                  type="button"
                  onClick={() => setOtpChannel('sms')}
                  className={`flex-1 py-1.5 px-2 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1 ${otpChannel === 'sms' ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground hover:text-primary'}`}
                  data-testid="otp-sms-channel-btn"
                >
                  <Smartphone className="w-3 h-3" /> Mobile
                </button>
              </div>

              {otpChannel === 'email' ? (
                <div>
                  <Label htmlFor="otp-email">Email Address</Label>
                  <Input id="otp-email" type="email" value={otpEmail} onChange={(e) => setOtpEmail(e.target.value)} required className="mt-1.5" data-testid="otp-email-input" placeholder="you@example.com" />
                </div>
              ) : (
                <div>
                  <Label htmlFor="otp-mobile">Mobile Number</Label>
                  <div className="flex mt-1.5 gap-2">
                    <div className="flex items-center px-3 bg-slate-50 border rounded-md text-sm text-muted-foreground">+91</div>
                    <Input id="otp-mobile" type="tel" value={otpMobile} onChange={(e) => setOtpMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} required className="flex-1" data-testid="otp-mobile-input" placeholder="9876543210" maxLength={10} />
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                {otpChannel === 'sms'
                  ? "We'll send a 6-digit OTP to your mobile number via SMS."
                  : "We'll send a 6-digit code to your email."}
              </p>

              <Button type="submit" disabled={loading} className="w-full h-11" data-testid="otp-send-btn">
                {otpChannel === 'sms' ? (
                  <><Phone className="w-4 h-4 mr-2" /> {loading ? 'Sending...' : 'Send SMS OTP'}</>
                ) : (
                  <><Mail className="w-4 h-4 mr-2" /> {loading ? 'Sending...' : 'Send Email OTP'}</>
                )}
              </Button>
            </form>
          )}

          {/* OTP Verify */}
          {mode === 'otp-verify' && (
            <form onSubmit={handleVerifyOTP} className="space-y-5" data-testid="otp-verify-form">
              <p className="text-sm text-center text-muted-foreground">
                Enter the 6-digit code sent to <strong>{otpTarget}</strong>
              </p>
              <div>
                <Label htmlFor="otp-code">OTP Code</Label>
                <Input id="otp-code" type="text" maxLength={6} value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))} required className="mt-1.5 text-center text-2xl tracking-[0.5em] font-mono" data-testid="otp-code-input" placeholder="------" />
              </div>
              <Button type="submit" disabled={loading || otpCode.length < 6} className="w-full h-11" data-testid="otp-verify-btn">
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </Button>
              <button type="button" onClick={handleResendOTP} disabled={loading} className="w-full text-xs text-secondary hover:underline" data-testid="otp-resend-btn">
                Didn't receive? Resend OTP
              </button>
              <button type="button" onClick={() => setMode('otp-request')} className="w-full text-xs text-muted-foreground hover:underline" data-testid="otp-change-btn">
                Change {otpChannel === 'sms' ? 'mobile number' : 'email'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Don't have an account? </span>
            <Link to="/signup" className="text-secondary font-medium hover:underline" data-testid="signup-link">Sign up</Link>
          </div>

          <div className="mt-4 pt-4 border-t flex justify-center gap-4">
            <a href="/admin/login" className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors" data-testid="admin-portal-hint">Admin Portal</a>
            <a href="/subadmin/login" className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors" data-testid="subadmin-portal-hint">Sub-Admin Portal</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
